import { useState, useCallback } from 'react';
import { notification } from 'antd';
import { useConnection } from '@sezenta/connection';
import { useBusinessContext } from '../../../context/BusinessContext';
import { Schedule, ScheduleFormData, ScheduleType, MonthlyType } from '../types';
import dayjs from 'dayjs';

// --- Normalizers for monthly config ---
const normalizeMonthly = (m: any) => {
  if (!m) return m;
  // Infer type if missing
  if (!m.type) {
    if (typeof m.date === 'number') {
      m.type = MonthlyType.ON_DATE; // 'onDate'
    } else if (m.day && m.ordinal) {
      m.type = MonthlyType.ON_DAY;  // 'onDay'
    }
  }
  // Uppercase ordinal to match enum (FIRST/SECOND/THIRD/FOURTH/LAST)
  if (m.ordinal && typeof m.ordinal === 'string') {
    m.ordinal = m.ordinal.toUpperCase();
  }
  return m;
};

const normalizeSchedules = (raw: any[]): Schedule[] =>
  (raw || []).map((s) => ({
    ...s,
    scheduleType: s.scheduleType as ScheduleType,
    startDate: s.startDate ? dayjs(s.startDate).toISOString() : null,
    endDate: s.endDate ? dayjs(s.endDate).toISOString() : null,
    collectionConfig: {
      ...s.collectionConfig,
      monthly: normalizeMonthly(s.collectionConfig?.monthly),
    },
  }));

export const useScheduleManagement = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const connection = useConnection();
  const { businessId } = useBusinessContext();

  const loadSchedules = useCallback(
    async (siteId: number) => {
      if (!siteId) return;

      setLoading(true);
      try {
        const response = await connection.get(
          `sites/${businessId}/${siteId}/schedules`,
        );
        setSchedules(normalizeSchedules(response.data || []));
      } catch (error) {
        console.error('Error loading schedules:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to load schedules',
        });
      } finally {
        setLoading(false);
      }
    },
    [connection, businessId],
  );

  const createSchedule = useCallback(
    async (siteId: number, values: ScheduleFormData) => {
      if (!siteId) return;

      const payload = {
        scheduleType: values.scheduleType,
        collectionConfig: {
          ...values.collectionConfig,
          monthly: values.collectionConfig?.monthly
            ? {
                ...values.collectionConfig.monthly,
                type: values.collectionConfig.monthly.type,
                ...(values.collectionConfig.monthly.type === MonthlyType.ON_DAY && 'ordinal' in values.collectionConfig.monthly
                  ? {
                      ordinal: values.collectionConfig.monthly.ordinal
                        ? String(values.collectionConfig.monthly.ordinal).toUpperCase()
                        : undefined,
                    }
                  : {}),
              }
            : undefined
        },
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate.format('YYYY-MM-DD'),
        description: values.description,
        isActive: values.isActive,
      };

      try {
        setSubmitting(true);
        await connection.post(
          `sites/${businessId}/${siteId}/schedules`,
          payload,
        );
        notification.success({
          message: 'Success',
          description: 'Schedule created successfully',
        });
        await loadSchedules(siteId);
        return true;
      } catch (error: any) {
        console.error('Error creating schedule:', error);
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Failed to create schedule',
        });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [connection, businessId, loadSchedules],
  );

  const updateSchedule = useCallback(
    async (siteId: number, scheduleId: string, values: ScheduleFormData) => {
      if (!siteId) return;

      const payload = {
        scheduleType: values.scheduleType,
        collectionConfig: {
          ...values.collectionConfig,
          monthly: values.collectionConfig?.monthly
            ? {
                ...values.collectionConfig.monthly,
                type: values.collectionConfig.monthly.type,
                ...(values.collectionConfig.monthly.type === MonthlyType.ON_DAY && 'ordinal' in values.collectionConfig.monthly
                  ? {
                      ordinal: values.collectionConfig.monthly.ordinal
                        ? String(values.collectionConfig.monthly.ordinal).toUpperCase()
                        : undefined,
                    }
                  : {}),
              }
            : undefined
        },
        startDate: values.startDate.format('YYYY-MM-DD'),
        endDate: values.endDate.format('YYYY-MM-DD'),
        description: values.description,
        isActive: values.isActive,
      };

      try {
        setSubmitting(true);
        await connection.patch(
          `sites/${businessId}/${siteId}/schedules/${scheduleId}`,
          payload,
        );
        notification.success({
          message: 'Success',
          description: 'Schedule updated successfully',
        });
        await loadSchedules(siteId);
        return true;
      } catch (error: any) {
        console.error('Error updating schedule:', error);
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Failed to update schedule',
        });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [connection, businessId, loadSchedules],
  );

  const deleteSchedule = useCallback(
    async (siteId: number, scheduleId: string) => {
      try {
        setDeleting(scheduleId);
        await connection.delete(
          `sites/${businessId}/${siteId}/schedules/${scheduleId}`,
        );
        notification.success({
          message: 'Success',
          description: 'Schedule deleted successfully',
        });
        await loadSchedules(siteId);
        return true;
      } catch (error) {
        console.error('Error deleting schedule:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to delete schedule',
        });
        return false;
      } finally {
        setDeleting(null);
      }
    },
    [connection, businessId, loadSchedules],
  );

  return {
    schedules,
    loading,
    submitting,
    deleting,
    loadSchedules,
    createSchedule,
    updateSchedule,
    deleteSchedule,
  };
};
